import { API } from "@/app/api";
import {
  IResponseWithStatus,
  IUserData,
  IUserPayoutMode,
  IUserSettings,
  IUserSettingsCoin,
} from "@/data/api/auth-data";
import { IUser2FaActivate, IWatcherLinkResponse } from "@/data/api/settings-data";
import {
  mapToDomainUserSettingsCoins,
} from "@/data/domain/mapper";
import {
  getResponseStatus,
  normalizeErrorStatus,
  postApi,
} from "./post-api";

export async function fetchUserSettings(
  sessionId: string | undefined,
  targetLogin: string | undefined,
  successCallback: (
    userSettings: Map<string, IUserSettingsCoin>,
    useNewSettingsApi: boolean
  ) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IUserSettings>(API.User.Settings, {
    id: sessionId,
    targetLogin: targetLogin,
  })
    .then((response) => {
      if (!response || response.status != API.Status.Success) {
        errorCallback(getResponseStatus(response));
        return;
      }

      const normalizedCoins = normalizeUserSettingsCoins(response.coins ?? []);
      const result = mapToDomainUserSettingsCoins(normalizedCoins);
      successCallback(result, true);
    })
    .catch((reason) => {
      console.warn(`fetchUserSettings catch error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchUserCredentials(
  sessionId: string | undefined,
  targetLogin: string | undefined,
  successCallback: (userSettings: IUserData) => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.User.Credentials, {
  //   method: "POST",
  //   body: JSON.stringify({
  //     id: sessionId,
  //     targetLogin: targetLogin,
  //   }),
  // })
  await postApi<IUserData>(API.User.Credentials, {
    id: sessionId,
    targetLogin: targetLogin,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback(response);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      console.warn(`fetchUserCredentials catch error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function updatePublicName(
  sessionId: string | undefined,
  targetLogin: string | undefined,
  name: string,
  successCallback: () => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.User.UpdateCredentials, {
  //   method: "POST",
  //   body: JSON.stringify({
  //     id: sessionId,
  //     targetLogin: targetLogin,
  //     name: name,
  //   }),
  // })
  await postApi<IResponseWithStatus>(API.User.UpdateCredentials, {
    id: sessionId,
    targetLogin: targetLogin,
    name: name,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback();
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      console.warn(`updatePublicName catch error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function updateSettings(
  coin: string,
  sessionId: string,
  targetLogin: string | undefined,
  changes: {
    payout?: {
      mode: IUserPayoutMode;
      address: string;
      instantPayoutThreshold: number;
    };
    miningMode?: "pplns" | "pps";
    autoExchangePayoutCoinName?: string;
  },
  successCallback: () => void,
  errorCallback: (status: string) => void
) {
  if (
    changes.payout == undefined &&
    changes.miningMode == undefined &&
    changes.autoExchangePayoutCoinName == undefined
  ) {
    successCallback();
    return;
  }

  const payloadNew: Record<string, unknown> = {
    coin: coin,
    id: sessionId,
    targetLogin: targetLogin,
  };
  const normalizedMiningMode =
    changes.miningMode != undefined
      ? normalizeMiningMode(changes.miningMode)
      : undefined;
  if (changes.miningMode != undefined && !normalizedMiningMode) {
    errorCallback("invalid_mining_mode");
    return;
  }
  if (changes.payout != undefined) {
    payloadNew.payout = {
      mode: changes.payout.mode,
      address: changes.payout.address,
      instantPayoutThreshold: String(changes.payout.instantPayoutThreshold),
    };
  }
  if (normalizedMiningMode != undefined) {
    payloadNew.mining = {
      mode: normalizedMiningMode,
    };
  }
  if (changes.autoExchangePayoutCoinName != undefined) {
    payloadNew.autoExchange = {
      payoutCoinName: changes.autoExchangePayoutCoinName,
    };
  }

  await requestUpdateSettings(payloadNew)
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback();
        return;
      }

      errorCallback(response?.status ?? API.Status.Unknown);
    })
    .catch((reason) => {
      console.warn(`updateSettings catch error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

interface IUserSettingsCoinNewApiShape {
  status?: string;
  name?: string;
  payout?: {
    mode?: string;
    address?: string | null;
    instantPayoutThreshold?: string | number | null;
  };
  mining?: {
    mode?: string;
  };
  autoExchange?: {
    payoutCoinName?: string;
  };
  pplnsFee?: number | string | null;
  ppsFee?: number | string | null;
}

function normalizeUserSettingsCoins(rawCoins: unknown[]): IUserSettingsCoin[] {
  return rawCoins
    .map((rawCoin) => normalizeUserSettingsCoin(rawCoin))
    .filter((coin): coin is IUserSettingsCoin => coin != undefined);
}

function normalizeUserSettingsCoin(
  rawCoin: unknown
): IUserSettingsCoin | undefined {
  if (!rawCoin || typeof rawCoin != "object" || Array.isArray(rawCoin)) {
    return undefined;
  }

  const coin = rawCoin as IUserSettingsCoinNewApiShape;
  const name = String(coin.name ?? "").trim();
  if (name.length == 0) return undefined;

  const payoutMode = parsePayoutMode(coin.payout?.mode);
  const instantPayoutThreshold = toNullableNumber(
    coin.payout?.instantPayoutThreshold
  );
  const miningMode = normalizeMiningMode(coin.mining?.mode);
  const payoutAddressRaw = coin.payout?.address;
  const autoExchangePayoutCoinName =
    typeof coin.autoExchange?.payoutCoinName == "string"
      ? coin.autoExchange.payoutCoinName
      : undefined;

  return {
    status: coin.status,
    name,
    address:
      typeof payoutAddressRaw == "string" || payoutAddressRaw == null
        ? (payoutAddressRaw ?? null)
        : String(payoutAddressRaw),
    payoutMode,
    instantPayoutThreshold,
    payoutThreshold: instantPayoutThreshold,
    autoPayoutEnabled: payoutMode != undefined ? payoutMode != "disabled" : undefined,
    miningMode,
    autoExchangePayoutCoinName,
    pplnsFee: toNullableNumber(coin.pplnsFee) ?? undefined,
    ppsFee: toNullableNumber(coin.ppsFee) ?? undefined,
  };
}

async function requestUpdateSettings(
  payload: Record<string, unknown>
): Promise<IResponseWithStatus> {
  return postApi<IResponseWithStatus>(API.User.UpdateSettings, payload);
}

function toNullableNumber(value: unknown): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parsePayoutMode(modeRaw: unknown): IUserPayoutMode | undefined {
  const mode = String(modeRaw ?? "").trim().toLowerCase();
  if (mode == "disabled" || mode == "regular" || mode == "instant") {
    return mode;
  }

  return undefined;
}

function normalizeMiningMode(value: unknown): "pplns" | "pps" | undefined {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized == "pplns" || normalized == "pps") {
    return normalized;
  }
  return undefined;
}

export async function initiate2fa(
  sessionId: string,
  successCallback: (key: string) => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.User.Activate2faInitiate, {
  //   method: "POST",
  //   body: JSON.stringify({ id: sessionId }),
  // })
  await postApi<IUser2FaActivate>(API.User.Activate2faInitiate, {
    id: sessionId,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback(response.key);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      console.warn(`initiate2fa catch error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function confirm2fa(
  _sessionId: string,
  totp: string,
  actionId: string,
  successCallback: () => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.User.Action, {
  //   method: "POST",
  //   body: JSON.stringify({
  //     actionId: actionId,
  //     totp: totp,
  //   }),
  // })
  await postApi<IResponseWithStatus>(API.User.Action, {
    actionId: actionId,
    totp: totp,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback();
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function deactivate2fa(
  sessionId: string,
  successCallback: () => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.User.Deactivate2faInitiate, {
  //   method: "POST",
  //   body: JSON.stringify({
  //     id: sessionId,
  //   }),
  // })
  await postApi<IResponseWithStatus>(API.User.Deactivate2faInitiate, {
    id: sessionId,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback();
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function getWatcherLink(
  sessionId: string,
  targetLogin: string | undefined,
  successCallback: (link: string) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IWatcherLinkResponse>(API.User.GetWatcherLink, {
    id: sessionId,
    targetLogin: targetLogin,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback(response.sessionid);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}
